import React, { useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  GripVertical,
  List,
  Loader2,
  Lock,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Button } from '@/components/ui/button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import {
  resolveOutlineReorderTarget,
  type NestedSitrepSection,
} from './nestSitrepSections';

type OutlineItemProps = {
  section: SitrepSectionType;
  active: boolean;
  sortable: boolean;
  isProcessing?: boolean;
  isLocked?: boolean;
  indent?: boolean;
  onSelect: () => void;
};

const outlineItemClassName = (active: boolean) =>
  cn(
    'tw:inline-flex tw:h-auto tw:min-h-4 tw:min-w-0 tw:flex-1 tw:items-center tw:justify-start tw:gap-1 tw:rounded-md tw:border-0 tw:bg-transparent tw:px-0 tw:py-0 tw:text-left tw:text-[12px]! tw:leading-4 tw:font-normal tw:text-muted-foreground tw:shadow-none',
    active && 'tw:text-foreground'
  );

const outlineListClassName =
  'tw:m-0 tw:flex tw:flex-col tw:gap-0.5 tw:p-0 tw:ps-0! tw:[padding-inline-start:0]';

const outlineRowClassName = (active = false, indent = false) =>
  cn(
    'tw:m-0 tw:flex tw:min-w-0 tw:items-center tw:gap-0.5 tw:rounded-md tw:p-1',
    indent ? 'tw:ps-5!' : 'tw:ps-1!',
    active && 'tw:bg-muted tw:text-foreground'
  );

const OutlineGrip: React.FC<{
  label: string;
  listeners?: object;
  attributes?: object;
  disabled?: boolean;
}> = ({ label, listeners, attributes, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    className={cn(
      'tw:m-0 tw:flex tw:h-3 tw:w-3 tw:shrink-0 tw:items-center tw:justify-start tw:overflow-hidden tw:p-0 tw:text-muted-foreground',
      disabled
        ? 'tw:cursor-default'
        : 'tw:cursor-grab tw:hover:text-foreground tw:active:cursor-grabbing'
    )}
    aria-label={label}
    {...attributes}
    {...listeners}
  >
    <GripVertical className="tw:size-3 tw:-ml-1" aria-hidden />
  </button>
);

const OutlineItemLabel: React.FC<{
  name: string;
  isAi?: boolean;
  isProcessing?: boolean;
  isLocked?: boolean;
}> = ({ name, isAi, isProcessing, isLocked }) => (
  <>
    {isAi ? <Sparkles className="tw:size-3 tw:shrink-0" /> : null}
    <span className="tw:min-w-0 tw:truncate tw:text-[12px] tw:leading-4 tw:font-normal">
      {name}
    </span>
    {isProcessing ? (
      <Loader2 className="tw:size-3 tw:shrink-0 tw:animate-spin tw:text-muted-foreground" />
    ) : null}
    {isLocked ? (
      <Lock className="tw:size-3 tw:shrink-0 tw:text-warning" />
    ) : null}
  </>
);

type CategoryOutlineLabelProps = {
  section: SitrepSectionType;
  sortable: boolean;
  active: boolean;
  expanded: boolean;
  onSelect: () => void;
  onToggleExpanded: () => void;
};

const CategoryOutlineLabel: React.FC<CategoryOutlineLabelProps> = ({
  section,
  sortable,
  active,
  expanded,
  onSelect,
  onToggleExpanded,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id, disabled: !sortable });

  return (
    <div
      role="listitem"
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(outlineRowClassName(active), isDragging && 'tw:opacity-40')}
    >
      <OutlineGrip
        label={`Reorder category ${section.name}`}
        disabled={!sortable}
        attributes={sortable ? attributes : undefined}
        listeners={sortable ? listeners : undefined}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        className="tw:shrink-0"
        aria-expanded={expanded}
        aria-label={
          expanded
            ? `Collapse ${section.name} subsections`
            : `Expand ${section.name} subsections`
        }
        onClick={onToggleExpanded}
      >
        {expanded ? <ChevronDown /> : <ChevronRight />}
      </Button>
      <button
        type="button"
        className={outlineItemClassName(active)}
        data-testid="reports-doc-outline-item"
        aria-current={active ? 'true' : undefined}
        onClick={onSelect}
      >
        <span className="tw:min-w-0 tw:truncate tw:text-[12px] tw:leading-4 tw:font-normal">
          {section.name}
        </span>
      </button>
    </div>
  );
};

const OutlineItem: React.FC<OutlineItemProps> = ({
  section,
  active,
  sortable,
  isProcessing,
  isLocked,
  indent = false,
  onSelect,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id, disabled: !sortable });

  return (
    <div
      role="listitem"
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        outlineRowClassName(active, indent),
        isDragging && 'tw:opacity-40'
      )}
    >
      <OutlineGrip
        label={`Reorder section ${section.name}`}
        disabled={!sortable}
        attributes={sortable ? attributes : undefined}
        listeners={sortable ? listeners : undefined}
      />
      <button
        type="button"
        className={outlineItemClassName(active)}
        data-testid="reports-doc-outline-item"
        aria-current={active ? 'true' : undefined}
        onClick={onSelect}
      >
        <OutlineItemLabel
          name={section.name}
          isAi={section.type === 'ai'}
          isProcessing={isProcessing}
          isLocked={isLocked}
        />
      </button>
    </div>
  );
};

type SitrepDocumentOutlineProps = {
  sections: SitrepSectionType[];
  nestedSections?: NestedSitrepSection[];
  activeSectionId: string | null;
  onSelect: (section: SitrepSectionType, index: number) => void;
  onReorder?: (section: SitrepSectionType, targetOrder: number) => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  lockedSectionIds?: ReadonlySet<string>;
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
};

const OutlineFilter: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => (
  <InputGroup className="tw:mb-2">
    <InputGroupAddon>
      <Search />
    </InputGroupAddon>
    <InputGroupInput
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Filter sections..."
      aria-label="Filter outline sections"
    />
    {value ? (
      <InputGroupAddon align="inline-end">
        <InputGroupButton
          size="icon-xs"
          aria-label="Clear outline filter"
          onClick={() => onChange('')}
        >
          <X />
        </InputGroupButton>
      </InputGroupAddon>
    ) : null}
  </InputGroup>
);

const isSectionProcessing = (section: SitrepSectionType) => {
  const status = section.section_versions?.[0]?.status?.toLowerCase() || '';
  return ['not started', 'fetching', 'uploading'].includes(status);
};

const SitrepDocumentOutline: React.FC<SitrepDocumentOutlineProps> = ({
  sections,
  nestedSections,
  activeSectionId,
  onSelect,
  onReorder,
  collapsed = false,
  onToggleCollapsed,
  lockedSectionIds,
  mobileOpen = false,
  onMobileOpenChange,
}) => {
  const [filter, setFilter] = useState('');
  const [collapsedCategoryIds, setCollapsedCategoryIds] = useState<Set<string>>(
    () => new Set()
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const outlineRoots =
    nestedSections ?? sections.map((s) => ({ ...s, children: [] }));
  const rootIds = useMemo(
    () => outlineRoots.map((section) => section.id),
    [outlineRoots]
  );
  const sectionsById = useMemo(() => {
    const map = new Map<string, SitrepSectionType>();
    for (const root of outlineRoots) {
      map.set(root.id, root);
      for (const child of root.children) map.set(child.id, child);
    }
    return map;
  }, [outlineRoots]);
  const trimmedFilter = filter.trim().toLowerCase();
  const canSort = !!onReorder && !trimmedFilter;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder || trimmedFilter) return;
    const target = resolveOutlineReorderTarget({
      nested: outlineRoots,
      activeId: String(active.id),
      overId: String(over.id),
    });
    if (!target) return;
    const section = sectionsById.get(target.sectionId);
    if (!section) return;
    onReorder(section, target.targetOrder);
  };

  const matchesFilter = (name: string) =>
    !trimmedFilter || name.toLowerCase().includes(trimmedFilter);

  const toggleCategoryExpanded = (sectionId: string) => {
    setCollapsedCategoryIds((current) => {
      const next = new Set(current);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const renderItems = () => (
    <div role="list" data-slot="outline-row" className={outlineListClassName}>
      {outlineRoots.map((section, index) => {
        const childSections =
          'children' in section && Array.isArray(section.children)
            ? section.children
            : [];
        const isCategory = childSections.length > 0;
        const visibleChildren = childSections.filter((child) =>
          matchesFilter(child.name)
        );
        if (!matchesFilter(section.name) && visibleChildren.length === 0) {
          return null;
        }
        const categoryExpanded =
          !collapsedCategoryIds.has(section.id) || !!trimmedFilter;
        return (
          <React.Fragment key={section.id}>
            {isCategory ? (
              <CategoryOutlineLabel
                section={section}
                sortable={canSort}
                active={section.id === activeSectionId}
                expanded={categoryExpanded}
                onSelect={() => onSelect(section, index)}
                onToggleExpanded={() => toggleCategoryExpanded(section.id)}
              />
            ) : (
              <OutlineItem
                section={section}
                active={section.id === activeSectionId}
                sortable={canSort}
                isProcessing={isSectionProcessing(section)}
                isLocked={lockedSectionIds?.has(section.id)}
                onSelect={() => onSelect(section, index)}
              />
            )}
            {categoryExpanded ? (
              <SortableContext
                items={visibleChildren.map((child) => child.id)}
                strategy={verticalListSortingStrategy}
              >
                {visibleChildren.map((child) => (
                  <OutlineItem
                    key={child.id}
                    section={child}
                    indent
                    active={child.id === activeSectionId}
                    sortable={canSort}
                    isProcessing={isSectionProcessing(child)}
                    isLocked={lockedSectionIds?.has(child.id)}
                    onSelect={() => onSelect(child, index)}
                  />
                ))}
              </SortableContext>
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderList = () =>
    onReorder ? (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={rootIds} strategy={verticalListSortingStrategy}>
          {renderItems()}
        </SortableContext>
      </DndContext>
    ) : (
      renderItems()
    );

  const rail = collapsed ? (
    <nav
      className="tw:sticky tw:top-0 tw:w-auto tw:shrink-0 tw:max-[992px]:hidden"
      aria-label="Report sections"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        title="Show outline"
        aria-label="Show outline"
        aria-expanded="false"
        data-testid="reports-doc-outline-expand"
        onClick={onToggleCollapsed}
      >
        <ChevronRight />
      </Button>
    </nav>
  ) : (
    <nav
      className="tw:sticky tw:top-0 tw:flex tw:min-h-0 tw:max-h-[70vh] tw:w-[200px] tw:shrink-0 tw:flex-col tw:overflow-hidden tw:border-r tw:border-border tw:pr-2 tw:max-[992px]:hidden"
      aria-label="Report sections"
    >
      {onToggleCollapsed && (
        <div className="tw:mb-2 tw:flex tw:items-center tw:gap-1">
          <span className="tw:min-w-0 tw:flex-1 tw:text-xs tw:text-muted-foreground">
            Outline
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            title="Hide outline"
            aria-label="Hide outline"
            aria-expanded="true"
            data-testid="reports-doc-outline-collapse"
            onClick={onToggleCollapsed}
          >
            <ChevronLeft />
          </Button>
        </div>
      )}
      <OutlineFilter value={filter} onChange={setFilter} />
      <ScrollArea
        className="tw:min-h-0 tw:flex-1"
        viewportClassName="tw:overflow-x-hidden tw:p-0 tw:ps-0"
      >
        {renderList()}
      </ScrollArea>
    </nav>
  );

  return (
    <>
      {rail}
      <div className="tw:mb-2 tw:hidden tw:max-[992px]:block">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onMobileOpenChange?.(true)}
        >
          <List />
          Outline
        </Button>
      </div>
      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent side="left" className="dark tw:w-72 tw:gap-0 tw:p-0">
          <SheetHeader className="tw:border-b tw:border-border tw:px-4 tw:py-3">
            <SheetTitle>Outline</SheetTitle>
          </SheetHeader>
          <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:p-3">
            <OutlineFilter value={filter} onChange={setFilter} />
            <ScrollArea
              className="tw:min-h-0 tw:flex-1"
              viewportClassName="tw:overflow-x-hidden tw:p-0 tw:ps-0"
            >
              {renderList()}
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default SitrepDocumentOutline;
